import { ReactNode } from 'react';
import { Search, Bell, User, ArrowLeft, MoreVertical, Plus, Filter, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { useNoshStore } from '../../lib/store';
import { daysUntilExpiry } from '../../lib/utils';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

type HeaderVariant = 'home' | 'page' | 'search' | 'profile';

interface MobileHeaderProps {
  variant?: HeaderVariant;
  title?: string;
  subtitle?: string;
  greeting?: string;
  showAvatar?: boolean;
  showSearch?: boolean;
  showNotifications?: boolean;
  showBack?: boolean;
  rightAction?: ReactNode;
  leftAction?: ReactNode;
  backgroundColor?: string;
  onSearch?: () => void;
  onBack?: () => void;
}

const MobileHeader = ({
  variant = 'page',
  title,
  subtitle,
  greeting,
  showAvatar = false,
  showSearch = false,
  showNotifications = false,
  showBack = false,
  rightAction,
  leftAction,
  backgroundColor = 'bg-white',
  onSearch,
  onBack
}: MobileHeaderProps) => {
  const { user, inventory } = useNoshStore();

  // Calculate food-relevant statistics
  const getInventoryStats = () => {
    const totalItems = inventory.length;
    const expiringItems = inventory.filter(item => {
      const days = daysUntilExpiry(item.expiryDate);
      return days >= 0 && days <= 3; // Expiring in 3 days or less
    }).length;
    const freshItems = inventory.filter(item => {
      const days = daysUntilExpiry(item.expiryDate);
      return days >= 7; // Fresh items with 7+ days
    }).length;

    return { totalItems, expiringItems, freshItems };
  };

  const { totalItems, expiringItems, freshItems } = getInventoryStats();

  // Home variant - special welcome header
  if (variant === 'home') {
    return (
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className="relative overflow-hidden rounded-3xl mx-4 mb-6 bg-gray-300 shadow-lg"
      >
        {/* Header Content */}
        <div className="relative z-10 p-4">
          {/* Top Bar - Bell and Search Icons Only */}
          <div className="flex items-center justify-end mb-1">
            <div className="flex items-center space-x-2">
              {showNotifications && (
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                >
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-10 w-10 bg-black/20 hover:bg-black/30 text-black border-0 rounded-full backdrop-blur-sm"
                  >
                    <Bell size={20} />
                    <div className="absolute -top-1 -right-1 w-3 h-3 bg-accent rounded-full border-2 border-black"></div>
                  </Button>
                </motion.div>
              )}
              
              {showSearch && (
                <motion.div
                  initial={{ scale: 0, rotate: 180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.6, type: "spring", stiffness: 200 }}
                >
                  <Link to="/profile">
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-10 w-10 bg-black/20 hover:bg-black/30 text-black border-0 rounded-full backdrop-blur-sm"
                    >
                      <Settings size={20} />
                    </Button>
                  </Link>
                </motion.div>
              )}
              
              {rightAction}
            </div>
          </div>
          
          {/* Main Content - Text and Avatar Side by Side */}
          <div className="flex items-start justify-between mb-3">
            {/* Left Side - Welcome Text */}
            <div className="flex-1 space-y-1">
              <motion.h1 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7, duration: 0.6 }}
                className="text-black text-2xl font-heading font-bold leading-tight"
              >
                {greeting || `What's cooking, ${user.name.split(' ')[0]}? 😋`}
              </motion.h1>
              
              <motion.p 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8, duration: 0.6 }}
                className="text-black/90 text-base leading-relaxed"
              >
                {subtitle || "Let's reduce food waste today!"}
              </motion.p>
            </div>
            
            {/* Right Side - Avatar */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              className="ml-2"
            >
              <div className="h-24 w-24 flex items-center justify-center">
                <img 
                  src={user.avatar} 
                  alt={user.name}
                  className="h-24 w-24 object-contain"
                />
              </div>
            </motion.div>
          </div>
          
          {/* Stats Bar */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.9, duration: 0.5 }}
            className="flex items-center justify-between bg-white rounded-2xl p-3"
          >
            <div className="text-center flex-1">
              <p className="text-black text-lg font-bold">{totalItems}</p>
              <p className="text-black/80 text-xs font-medium">Total Items</p>
            </div>
            <div className="w-px h-8 bg-black/30"></div>
            <div className="text-center flex-1">
              <p className="text-black text-lg font-bold">{expiringItems}</p>
              <p className="text-black/80 text-xs font-medium">Expiring</p>
            </div>
            <div className="w-px h-8 bg-black/30"></div>
            <div className="text-center flex-1">
              <p className="text-black text-lg font-bold">{freshItems}</p>
              <p className="text-black/80 text-xs font-medium">Fresh</p>
            </div>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // Profile variant - clean header with user info
  if (variant === 'profile') {
    return (
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`${backgroundColor} mx-4 mb-6 rounded-2xl shadow-sm border border-gray-100/50`}
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              {showAvatar && (
                <div className="h-16 w-16 flex items-center justify-center border-2 border-gray-100 rounded-xl bg-gray-50">
                  <img 
                    src={user.avatar} 
                    alt={user.name}
                    className="h-14 w-14 object-contain"
                  />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-heading font-bold text-gray-900">{title}</h1>
                {subtitle && (
                  <p className="text-gray-600 text-sm mt-1">{subtitle}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {rightAction}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Search variant - focused on search functionality
  if (variant === 'search') {
    return (
      <motion.div 
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className={`${backgroundColor} mx-4 mb-6 rounded-2xl shadow-sm border border-gray-100/50`}
      >
        <div className="p-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              {showBack && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 rounded-full"
                  onClick={onBack}
                >
                  <ArrowLeft size={18} />
                </Button>
              )}
              {leftAction}
              <h1 className="text-xl font-heading font-semibold text-gray-900">{title}</h1>
            </div>
            
            <div className="flex items-center space-x-2">
              {showSearch && (
                <Button 
                  variant="ghost" 
                  size="icon" 
                  className="h-8 w-8 rounded-full bg-gray-50 hover:bg-gray-100"
                  onClick={onSearch}
                >
                  <Search size={18} />
                </Button>
              )}
              {rightAction}
            </div>
          </div>
          
          {subtitle && (
            <p className="text-gray-600 text-sm">{subtitle}</p>
          )}
        </div>
      </motion.div>
    );
  }

  // Default page variant - clean and minimal
  return (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className={`${backgroundColor} mx-4 mb-6 rounded-2xl shadow-sm border border-gray-100/50`}
    >
      <div className="p-4 px-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            {showBack && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full"
                onClick={onBack}
              >
                <ArrowLeft size={18} />
              </Button>
            )}
            {leftAction}
            <div>
              <h1 className="text-xl font-heading font-semibold text-gray-900">{title}</h1>
              {subtitle && (
                <p className="text-gray-600 text-sm mt-0.5">{subtitle}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {showSearch && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full bg-gray-50 hover:bg-gray-100"
                onClick={onSearch}
              >
                <Search size={18} />
              </Button>
            )}
            {showNotifications && (
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8 rounded-full bg-gray-50 hover:bg-gray-100 relative"
              >
                <Bell size={18} />
                <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full"></div>
              </Button>
            )}
            {rightAction}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default MobileHeader; 